import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import CurrencyInput from 'react-currency-input-field';
import db from '../../db/db';
import { setAlert } from '../../actions/alert';
import { CustomValueContainer } from '../../components/react_select/ReactSelect';
import TicketLiquidacion from './TicketLiquidacion';
import TicketPago from './TicketPago';

// ─── Estilos React Select ────────────────────────────────────────────────────
const selectStyles = {
    control: (base, state) => ({
        ...base,
        background: '#1F1F1F',
        borderColor: state.isFocused ? '#F5C800' : '#2E2E2E',
        boxShadow: state.isFocused ? '0 0 0 2px rgba(245,200,0,0.15)' : 'none',
        borderRadius: '10px',
        minHeight: '46px',
        '&:hover': { borderColor: '#F5C800' },
    }),
    menu: (base) => ({ ...base, background: '#1A1A1A', border: '1px solid #2E2E2E', borderRadius: '10px', zIndex: 99 }),
    option: (base, state) => ({
        ...base,
        background: state.isSelected ? 'rgba(245,200,0,0.15)' : state.isFocused ? '#222' : 'transparent',
        color: state.isSelected ? '#F5C800' : '#C0C0C0',
        cursor: 'pointer',
    }),
    singleValue: (base) => ({ ...base, color: '#F0F0F0' }),
    placeholder: (base) => ({ ...base, color: '#555' }),
    input: (base) => ({ ...base, color: '#F0F0F0' }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({ ...base, color: '#555' }),
    clearIndicator: (base) => ({ ...base, color: '#555' }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const currencyStyle = (focused) => ({
    background: '#1F1F1F',
    border: `2px solid ${focused ? '#F5C800' : '#2E2E2E'}`,
    boxShadow: focused ? '0 0 0 2px rgba(245,200,0,0.15)' : 'none',
    borderRadius: '10px',
    padding: '0.75rem 1rem',
    color: '#F0F0F0',
    fontSize: '1rem',
    fontWeight: 600,
    width: '100%',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    fontFamily: 'inherit',
});

const fmt = (n) => (n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ─── Componente principal ────────────────────────────────────────────────────
const LiquidarVueltas = () => {
    const dispatch = useDispatch();

    const [empleadoLogueado, setEmpleadoLogueado] = useState(null);
    const [eventoActivo, setEventoActivo] = useState(null);
    const [sucursalNombre, setSucursalNombre] = useState('');
    const [vueltas, setVueltas] = useState([]);
    const [pagos, setPagos] = useState([]);
    const [metodos, setMetodos] = useState([]);
    const [empleados, setEmpleados] = useState([]);

    // Selección de vueltas
    const [seleccionadas, setSeleccionadas] = useState(new Set());

    // Ticket liquidación
    const [mostrarTicket, setMostrarTicket] = useState(false);
    const [ticketData, setTicketData] = useState(null);

    // Ticket pago
    const [ticketPago, setTicketPago] = useState(null);

    // Modal agregar pago
    const [modalPago, setModalPago] = useState(false);
    const [pagoForm, setPagoForm] = useState({ metodo: null, total: '', vendedor: null });
    const [pagoFocused, setPagoFocused] = useState(false);
    const [statusPago, setStatusPago] = useState(false);

    // Modal eliminar pago
    const [modalEliminar, setModalEliminar] = useState(null);

    // Filtro por empleado
    const [filtroEmpleado, setFiltroEmpleado] = useState(null);

    // IDs de empleados que asistieron hoy
    const [idsAsistieron, setIdsAsistieron] = useState(new Set());

    // Modal confirmar liquidación
    const [modalLiquidar, setModalLiquidar] = useState(false);
    const [statusLiquidar, setStatusLiquidar] = useState(false);

    // ── Carga ────────────────────────────────────────────────────────────────
    const cargar = useCallback(async () => {
        const sesion = await db.sesion.toArray();
        const logueado = sesion[0];
        setEmpleadoLogueado(logueado);

        const sucursalId = logueado.sucursal?._id || logueado.sucursal;
        const sucursalReg = await db.sucursales
            .filter(s => String(s._id || s.id) === String(sucursalId))
            .first();
        setEventoActivo(sucursalReg?.evento || null);
        setSucursalNombre(sucursalReg?.nombre || '');

        const [vs, pgs, mets, emps, todosAsistencias] = await Promise.all([
            db.vueltas.filter(v => v.status === false && !v.corte_local_id && !v.cancelada
                && String(v.sucursal?._id || v.sucursal) === String(sucursalId)).toArray(),
            db.pagos_vueltas.filter(p => !p.corte_local_id
                && String(p.sucursal?._id || p.sucursal) === String(sucursalId)).toArray(),
            db.metodos.toArray(),
            db.empleados.toArray(),
            db.asistencias.toArray(),
        ]);

        const paseReciente = todosAsistencias.sort((a, b) => new Date(b.fecha) - new Date(a.fecha))[0] || null;
        const ids = new Set(
            (paseReciente?.empleados || []).filter(e => e.asistio === true).map(e => e.empleado)
        );

        setVueltas(vs.sort((a, b) => new Date(b.fecha) - new Date(a.fecha)));
        setPagos(pgs.sort((a, b) => new Date(b.fecha) - new Date(a.fecha)));
        setMetodos(mets.map(m => ({ value: m._id, label: m.nombre, ...m })));
        setEmpleados(emps);
        setIdsAsistieron(ids);
    }, []);

    useEffect(() => { cargar(); }, [cargar]);

    // ── Helpers ──────────────────────────────────────────────────────────────
    const resolverEmpleado = (empId) => {
        if (!empId) return '—';
        const emp = empleados.find(e => e._id === empId || String(e.id) === String(empId));
        if (!emp) return '—';
        return `${emp.nombre} ${emp.apellido_paterno || ''}`.trim();
    };

    const resolverMetodo = (metodoId) => {
        const met = metodos.find(m => m._id === metodoId || String(m.id) === String(metodoId));
        return met?.label || '—';
    };

    // ── Listas filtradas por empleado seleccionado ───────────────────────────
    const vueltasFiltradas = filtroEmpleado
        ? vueltas.filter(v => v.vendedor === filtroEmpleado.value)
        : vueltas;

    const pagosFiltrados = filtroEmpleado
        ? pagos.filter(p => p.vendedor === filtroEmpleado.value)
        : pagos;

    // ── Empleados que figuran en vueltas pendientes (para filtro) ───────────
    const empleadosEnVueltas = (() => {
        const idsUnicos = [...new Set(vueltas.map(v => v.vendedor).filter(Boolean))];
        return idsUnicos.map(id => {
            const emp = empleados.find(e => e._id === id || String(e.id) === String(id));
            const label = emp ? `${emp.nombre} ${emp.apellido_paterno || ''}`.trim() : id;
            return { value: id, label };
        }).sort((a, b) => a.label.localeCompare(b.label, 'es'));
    })();

    // ── Selección ────────────────────────────────────────────────────────────
    const toggleSeleccion = (id) => {
        setSeleccionadas(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const todasSeleccionadas = vueltasFiltradas.length > 0
        && vueltasFiltradas.every(v => seleccionadas.has(v.id));

    const toggleTodas = () => {
        if (todasSeleccionadas) {
            setSeleccionadas(prev => {
                const next = new Set(prev);
                vueltasFiltradas.forEach(v => next.delete(v.id));
                return next;
            });
        } else {
            setSeleccionadas(prev => {
                const next = new Set(prev);
                vueltasFiltradas.forEach(v => next.add(v.id));
                return next;
            });
        }
    };

    // ── Vendedores ambulantes (tipo === 4) de la sucursal activa que asistieron ──
    const vendedoresOpts = (() => {
        const sucursalId = empleadoLogueado?.sucursal?._id || empleadoLogueado?.sucursal;
        return empleados
            .filter(e => {
                if (e.tipo !== 4) return false;
                if (!idsAsistieron.has(e._id)) return false;
                const ids = Array.isArray(e.sucursales) && e.sucursales.length > 0
                    ? e.sucursales.map(s => String(s?._id || s))
                    : [String(e.sucursal?._id || e.sucursal)];
                return ids.includes(String(sucursalId));
            })
            .map(e => ({ value: e._id, label: `${e.nombre} ${e.apellido_paterno || ''}`.trim(), ...e }));
    })();

    // ── Totales ──────────────────────────────────────────────────────────────
    const vueltasSeleccionadas    = vueltas.filter(v => seleccionadas.has(v.id));
    const totalSeleccionadas      = vueltasSeleccionadas.reduce((acc, v) => acc + (v.total || 0), 0);
    const vendedoresSeleccionados = new Set(vueltasSeleccionadas.map(v => v.vendedor).filter(Boolean));
    const pagosRelevantes         = seleccionadas.size > 0
        ? pagos.filter(p => vendedoresSeleccionados.has(p.vendedor))
        : [];
    const totalPagos              = pagosRelevantes.reduce((acc, p) => acc + (p.total || 0), 0);
    const balance                 = totalPagos - totalSeleccionadas;
    const saldado                 = seleccionadas.size > 0;

    // ── Agregar pago ─────────────────────────────────────────────────────────
    const handleAbrirPago = () => {
        setPagoForm({ metodo: null, total: '', vendedor: filtroEmpleado || null });
        setModalPago(true);
    };

    const handleGuardarPago = async () => {
        if (!pagoForm.vendedor) return dispatch(setAlert('Selecciona el vendedor', 'danger'));
        if (!pagoForm.metodo) return dispatch(setAlert('Selecciona un método de pago', 'danger'));
        if (!pagoForm.total || parseFloat(pagoForm.total) <= 0)
            return dispatch(setAlert('Ingresa un monto válido', 'danger'));

        setStatusPago(true);
        try {
            const fechaPago = new Date();
            await db.pagos_vueltas.add({
                sucursal:    empleadoLogueado.sucursal?._id || empleadoLogueado.sucursal,
                metodo:      pagoForm.metodo._id,
                vendedor:    pagoForm.vendedor._id || pagoForm.vendedor.value,
                responsable: empleadoLogueado._id,
                evento:      eventoActivo?._id || null,
                fecha:       fechaPago,
                total:       parseFloat(pagoForm.total),
            });

            const vendedorOpt = vendedoresOpts.find(o => o.value === (pagoForm.vendedor._id || pagoForm.vendedor.value));
            setTicketPago({
                vendedor:    vendedorOpt?.label || pagoForm.vendedor.label || '—',
                responsable: `${empleadoLogueado.nombre || ''} ${empleadoLogueado.apellido_paterno || ''}`.trim(),
                fecha:       fechaPago,
                metodo:      pagoForm.metodo.label || pagoForm.metodo.nombre || '—',
                total:       parseFloat(pagoForm.total),
            });

            setModalPago(false);
            await cargar();
        } catch {
            dispatch(setAlert('Error al registrar el pago', 'danger'));
        } finally {
            setStatusPago(false);
        }
    };

    const handleEliminarPago = async () => {
        setStatusPago(true);
        try {
            await db.pagos_vueltas.delete(modalEliminar.id);
            setModalEliminar(null);
            await cargar();
        } catch {
            dispatch(setAlert('Error al eliminar el pago', 'danger'));
        } finally {
            setStatusPago(false);
        }
    };

    // ── Liquidar todas ───────────────────────────────────────────────────────
    const handleLiquidar = async () => {
        setStatusLiquidar(true);
        try {
            // Snapshot antes de que cargar() vacíe los estados
            const vendedoresUnicos = [...new Set(vueltasSeleccionadas.map(v => v.vendedor).filter(Boolean))];
            const vendedorStr = vendedoresUnicos.map(id => resolverEmpleado(id)).join(', ') || '—';

            const metodosTotales = pagosRelevantes.reduce((acc, p) => {
                const nombre = resolverMetodo(p.metodo);
                const existing = acc.find(x => x.nombre === nombre);
                if (existing) existing.total += p.total || 0;
                else acc.push({ nombre, total: p.total || 0 });
                return acc;
            }, []);

            const snapshot = {
                responsable:  `${empleadoLogueado?.nombre || ''} ${empleadoLogueado?.apellido_paterno || ''}`.trim() || '—',
                vendedor:     vendedorStr,
                sucursal:     sucursalNombre,
                fecha:        new Date(),
                numVentas:    vueltasSeleccionadas.length,
                totalACobrar: totalSeleccionadas,
                totalPagado:  totalPagos,
                metodos:      metodosTotales,
            };

            await Promise.all(
                vueltasSeleccionadas.map(v => db.vueltas.update(v.id, { status: true }))
            );
            const n = vueltasSeleccionadas.length;
            dispatch(setAlert(`${n} venta${n > 1 ? 's' : ''} ambulante${n > 1 ? 's' : ''} liquidada${n > 1 ? 's' : ''} correctamente`, 'success'));
            setModalLiquidar(false);
            await cargar();
            setSeleccionadas(new Set());

            setTicketData(snapshot);
            setMostrarTicket(true);
        } catch {
            dispatch(setAlert('Error al liquidar las ventas ambulantes', 'danger'));
        } finally {
            setStatusLiquidar(false);
        }
    };

    return (
        <>
            {mostrarTicket && ticketData && (
                <TicketLiquidacion data={ticketData} onCerrar={() => setMostrarTicket(false)} />
            )}

            {ticketPago && (
                <TicketPago data={ticketPago} onCerrar={() => setTicketPago(null)} />
            )}

            <div style={styles.page}>

                {/* ── Encabezado ── */}
                <div style={styles.header}>
                    <i className="fas fa-money-bill-wave me-3" style={{ color: '#F5C800', fontSize: '1.3rem' }}></i>
                    <h4 style={styles.titulo}>Liquidar ventas ambulantes</h4>
                </div>

                {/* ── Indicadores ── */}
                <div style={styles.indicadoresGrid}>
                    <div style={styles.indicador}>
                        <span style={styles.indLabel}>Seleccionadas</span>
                        <span style={styles.indNum}>
                            {seleccionadas.size}
                            <span style={{ color: '#444', fontWeight: 500, fontSize: '1rem' }}> / {vueltas.length}</span>
                        </span>
                    </div>
                    <div style={styles.indicador}>
                        <span style={styles.indLabel}>Monto a cobrar</span>
                        <span style={{ ...styles.indNum, color: '#F5C800' }}>${fmt(totalSeleccionadas)}</span>
                    </div>
                    <div style={styles.indicador}>
                        <span style={styles.indLabel}>Total pagos registrados</span>
                        <span style={{ ...styles.indNum, color: totalPagos >= totalSeleccionadas && seleccionadas.size > 0 ? '#48BB78' : '#F0F0F0' }}>
                            ${fmt(totalPagos)}
                        </span>
                    </div>
                    <div style={{ ...styles.indicador, borderColor: balance >= 0 && seleccionadas.size > 0 ? 'rgba(72,187,120,0.3)' : '#2E2E2E' }}>
                        <span style={styles.indLabel}>Balance</span>
                        <span style={{ ...styles.indNum, color: balance >= 0 && seleccionadas.size > 0 ? '#48BB78' : '#E53E3E' }}>
                            {balance >= 0 ? '+' : ''}{fmt(balance)}
                        </span>
                    </div>
                </div>

                {/* ── Filtro por empleado ── */}
                <div style={styles.filtroBar}>
                    <i className="fas fa-filter" style={{ color: '#555', fontSize: '0.85rem', flexShrink: 0 }}></i>
                    <div style={{ flex: 1, maxWidth: '280px' }}>
                        <Select
                            options={empleadosEnVueltas}
                            value={filtroEmpleado}
                            onChange={val => setFiltroEmpleado(val)}
                            isClearable
                            isSearchable={false}
                            placeholder="Todos los empleados"
                            styles={{
                                ...selectStyles,
                                control: (base, state) => ({
                                    ...selectStyles.control(base, state),
                                    minHeight: '38px',
                                }),
                            }}
                            components={{ ValueContainer: CustomValueContainer }}
                            menuPortalTarget={document.body}
                        />
                    </div>
                    {filtroEmpleado && (
                        <span style={styles.filtroBadge}>
                            {vueltasFiltradas.length} venta{vueltasFiltradas.length !== 1 ? 's' : ''}
                        </span>
                    )}
                </div>

                {/* ── Cuerpo principal ── */}
                <div style={styles.dosCols}>

                    {/* ── Ventas ambulantes pendientes ── */}
                    <div style={styles.card}>
                        <div style={{ ...styles.cardHeader, justifyContent: 'space-between' }}>
                            <span>
                                <i className="fas fa-redo me-2" style={{ color: '#F5C800' }}></i>
                                Ventas ambulantes pendientes
                            </span>
                            {vueltasFiltradas.length > 0 && (
                                <button style={styles.btnSelTodas} onClick={toggleTodas}>
                                    {todasSeleccionadas ? 'Deseleccionar todas' : 'Seleccionar todas'}
                                </button>
                            )}
                        </div>
                        {vueltasFiltradas.length === 0 ? (
                            <div style={styles.vacio}>
                                <i className="fas fa-check-circle" style={{ fontSize: '2rem', color: '#48BB78', marginBottom: '0.5rem' }}></i>
                                <p style={{ color: '#888', margin: 0, fontSize: '0.9rem' }}>No hay ventas ambulantes pendientes</p>
                            </div>
                        ) : (
                            vueltasFiltradas.map((v, idx) => {
                                const sel = seleccionadas.has(v.id);
                                const d   = new Date(v.fecha);
                                return (
                                    <div
                                        key={v.id}
                                        style={{
                                            ...styles.vueltaFila,
                                            borderBottom: idx < vueltasFiltradas.length - 1 ? '1px solid #222' : 'none',
                                            background: sel ? 'rgba(245,200,0,0.04)' : 'transparent',
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => toggleSeleccion(v.id)}
                                    >
                                        <div style={sel ? styles.checkOn : styles.checkOff}>
                                            {sel && <i className="fas fa-check" style={{ fontSize: '0.6rem', color: '#111' }}></i>}
                                        </div>
                                        <div style={styles.vueltaInfo}>
                                            <span style={styles.vueltaFolio}>{v.folio}</span>
                                            <span style={styles.vueltaMeta}>
                                                {resolverEmpleado(v.vendedor)} · {d.toLocaleDateString('es-MX')} {d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                        </div>
                                        <span style={{ ...styles.vueltaTotal, color: sel ? '#F5C800' : '#F0F0F0' }}>${fmt(v.total)}</span>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* ── Pagos registrados ── */}
                    <div style={styles.card}>
                        <div style={{ ...styles.cardHeader, justifyContent: 'space-between' }}>
                            <span>
                                <i className="fas fa-coins me-2" style={{ color: '#F5C800' }}></i>
                                Pagos registrados
                            </span>
                            <button style={styles.btnAgregar} onClick={handleAbrirPago}>
                                <i className="fas fa-plus me-1"></i>
                                Pago
                            </button>
                        </div>
                        {pagosFiltrados.length === 0 ? (
                            <div style={styles.vacio}>
                                <i className="fas fa-receipt" style={{ fontSize: '2rem', color: '#333', marginBottom: '0.5rem' }}></i>
                                <p style={{ color: '#888', margin: 0, fontSize: '0.9rem' }}>Sin pagos registrados</p>
                            </div>
                        ) : (
                            pagosFiltrados.map((p, idx) => (
                                <div key={p.id} style={{
                                    ...styles.pagoFila,
                                    borderBottom: idx < pagosFiltrados.length - 1 ? '1px solid #222' : 'none',
                                }}>
                                    <div style={styles.pagoInfo}>
                                        <span style={styles.pagoMonto}>${fmt(p.total)}</span>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
                                            <span style={styles.pagoMet}>{resolverMetodo(p.metodo)}</span>
                                            <span style={styles.pagoVendedor}>
                                                <i className="fas fa-user me-1"></i>
                                                {resolverEmpleado(p.vendedor)}
                                            </span>
                                        </div>
                                    </div>
                                    <button
                                        style={styles.btnEliminar}
                                        onClick={() => setModalEliminar(p)}
                                        title="Eliminar pago"
                                    >
                                        <i className="fas fa-trash"></i>
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* ── Botón liquidar ── */}
                <div style={{ marginTop: '1.5rem' }}>
                    <button
                        style={!saldado
                            ? { ...styles.btnLiquidar, opacity: 0.4, cursor: 'not-allowed' }
                            : styles.btnLiquidar
                        }
                        onClick={() => saldado && setModalLiquidar(true)}
                        disabled={!saldado}
                    >
                        <i className="fas fa-check-double me-2"></i>
                        Liquidar las ventas seleccionadas
                        {seleccionadas.size > 0 && ` (${seleccionadas.size})`}
                    </button>
                    {seleccionadas.size === 0 && vueltas.length > 0 && (
                        <p style={styles.aviso}>Selecciona al menos una venta para liquidar.</p>
                    )}
                </div>
            </div>

            {/* ── Modal agregar pago ── */}
            {modalPago && (
                <div style={modal.overlay} onClick={() => !statusPago && setModalPago(false)}>
                    <div style={modal.card} onClick={e => e.stopPropagation()}>
                        <h5 style={modal.titulo}>Registrar pago</h5>

                        <div style={styles.campo}>
                            <label style={styles.label}>Vendedor</label>
                            <Select
                                options={vendedoresOpts}
                                value={pagoForm.vendedor}
                                onChange={val => setPagoForm(f => ({ ...f, vendedor: val }))}
                                isClearable
                                isSearchable
                                placeholder="Selecciona un vendedor"
                                styles={selectStyles}
                                components={{ ValueContainer: CustomValueContainer }}
                                menuPortalTarget={document.body}
                            />
                        </div>

                        <div style={{ ...styles.campo, marginTop: '1rem' }}>
                            <label style={styles.label}>Método de pago</label>
                            <Select
                                options={metodos}
                                value={pagoForm.metodo}
                                onChange={val => setPagoForm(f => ({ ...f, metodo: val }))}
                                isClearable
                                isSearchable={false}
                                placeholder="Selecciona un método"
                                styles={selectStyles}
                                components={{ ValueContainer: CustomValueContainer }}
                                menuPortalTarget={document.body}
                            />
                        </div>

                        <div style={{ ...styles.campo, marginTop: '1rem' }}>
                            <label style={styles.label}>Monto</label>
                            <CurrencyInput
                                value={pagoForm.total || ''}
                                onValueChange={val => setPagoForm(f => ({ ...f, total: val || '' }))}
                                prefix="$"
                                decimalsLimit={2}
                                groupSeparator=","
                                decimalSeparator="."
                                placeholder="$0.00"
                                onFocus={() => setPagoFocused(true)}
                                onBlur={() => setPagoFocused(false)}
                                style={currencyStyle(pagoFocused)}
                            />
                        </div>

                        <div style={{ ...modal.botones, marginTop: '1.5rem' }}>
                            <button style={modal.btnNo} onClick={() => setModalPago(false)} disabled={statusPago}>
                                Cancelar
                            </button>
                            <button
                                style={statusPago ? { ...modal.btnSi, opacity: 0.6, cursor: 'not-allowed' } : modal.btnSi}
                                onClick={handleGuardarPago}
                                disabled={statusPago}
                            >
                                {statusPago ? (
                                    <><span className="spinner-border spinner-border-sm me-2" role="status" />Guardando...</>
                                ) : 'Guardar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Modal eliminar pago ── */}
            {modalEliminar && (
                <div style={modal.overlay} onClick={() => !statusPago && setModalEliminar(null)}>
                    <div style={modal.card} onClick={e => e.stopPropagation()}>
                        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                            <i className="fas fa-trash" style={{ color: '#E53E3E', fontSize: '2rem' }}></i>
                        </div>
                        <h5 style={{ ...modal.titulo, textAlign: 'center' }}>¿Eliminar pago?</h5>
                        <p style={{ color: '#888', textAlign: 'center', fontSize: '0.95rem', marginBottom: '1.5rem' }}>
                            Se eliminará el pago de{' '}
                            <strong style={{ color: '#F0F0F0' }}>${fmt(modalEliminar.total)}</strong>{' '}
                            ({resolverMetodo(modalEliminar.metodo)}).
                        </p>
                        <div style={modal.botones}>
                            <button style={modal.btnNo} onClick={() => setModalEliminar(null)} disabled={statusPago}>
                                Cancelar
                            </button>
                            <button
                                style={statusPago ? { ...modal.btnEliminar, opacity: 0.6, cursor: 'not-allowed' } : modal.btnEliminar}
                                onClick={handleEliminarPago}
                                disabled={statusPago}
                            >
                                {statusPago ? (
                                    <><span className="spinner-border spinner-border-sm me-2" role="status" />Eliminando...</>
                                ) : 'Eliminar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Modal confirmar liquidación ── */}
            {modalLiquidar && (
                <div style={modal.overlay} onClick={() => !statusLiquidar && setModalLiquidar(false)}>
                    <div style={modal.card} onClick={e => e.stopPropagation()}>
                        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                            <i className="fas fa-check-double" style={{ color: '#48BB78', fontSize: '2rem' }}></i>
                        </div>
                        <h5 style={{ ...modal.titulo, textAlign: 'center' }}>¿Liquidar ventas seleccionadas?</h5>
                        <p style={{ color: '#888', textAlign: 'center', fontSize: '0.95rem', marginBottom: '1.5rem', lineHeight: 1.6 }}>
                            Se finalizarán <strong style={{ color: '#F0F0F0' }}>{seleccionadas.size}</strong> venta{seleccionadas.size > 1 ? 's' : ''} ambulante{seleccionadas.size > 1 ? 's' : ''} por un total de{' '}
                            <strong style={{ color: '#F5C800' }}>${fmt(totalSeleccionadas)}</strong>.
                            Esta acción no se puede deshacer.
                        </p>
                        <div style={modal.botones}>
                            <button style={modal.btnNo} onClick={() => setModalLiquidar(false)} disabled={statusLiquidar}>
                                Cancelar
                            </button>
                            <button
                                style={statusLiquidar ? { ...modal.btnLiquidar, opacity: 0.6, cursor: 'not-allowed' } : modal.btnLiquidar}
                                onClick={handleLiquidar}
                                disabled={statusLiquidar}
                            >
                                {statusLiquidar ? (
                                    <><span className="spinner-border spinner-border-sm me-2" role="status" />Liquidando...</>
                                ) : 'Sí, liquidar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

// ─── Estilos ─────────────────────────────────────────────────────────────────
const styles = {
    page: {
        maxWidth: '1100px',
        margin: '0 auto',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '1.5rem',
        paddingBottom: '1rem',
        borderBottom: '1px solid #2E2E2E',
    },
    titulo: {
        color: '#F0F0F0',
        fontWeight: 700,
        margin: 0,
    },
    indicadoresGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '0.75rem',
        marginBottom: '1.5rem',
    },
    indicador: {
        background: '#1A1A1A',
        border: '1px solid #2E2E2E',
        borderRadius: '14px',
        padding: '1.1rem 1.25rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.4rem',
    },
    indLabel: {
        color: '#666',
        fontSize: '0.78rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    indNum: {
        color: '#F0F0F0',
        fontWeight: 800,
        fontSize: '1.5rem',
    },
    dosCols: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '1.25rem',
    },
    card: {
        background: '#1A1A1A',
        border: '1px solid #2E2E2E',
        borderRadius: '14px',
        overflow: 'hidden',
    },
    cardHeader: {
        display: 'flex',
        alignItems: 'center',
        color: '#F0F0F0',
        fontWeight: 700,
        fontSize: '0.95rem',
        padding: '1rem 1.25rem',
        borderBottom: '1px solid #2E2E2E',
        background: '#111',
    },
    vacio: {
        padding: '2.5rem',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    btnSelTodas: {
        background: 'transparent',
        border: '1px solid #2E2E2E',
        borderRadius: '7px',
        padding: '0.25rem 0.7rem',
        color: '#888',
        fontSize: '0.78rem',
        fontWeight: 600,
        cursor: 'pointer',
        flexShrink: 0,
    },
    checkOff: {
        width: '18px',
        height: '18px',
        borderRadius: '5px',
        border: '1.5px solid #333',
        background: '#1A1A1A',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    checkOn: {
        width: '18px',
        height: '18px',
        borderRadius: '5px',
        border: '1.5px solid #F5C800',
        background: '#F5C800',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    vueltaFila: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.85rem 1.25rem',
        gap: '0.75rem',
    },
    vueltaInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.2rem',
        flex: 1,
        minWidth: 0,
    },
    vueltaFolio: {
        color: '#F5C800',
        fontWeight: 700,
        fontFamily: 'monospace',
        fontSize: '0.95rem',
    },
    vueltaMeta: {
        color: '#666',
        fontSize: '0.8rem',
    },
    vueltaTotal: {
        color: '#F0F0F0',
        fontWeight: 700,
        fontSize: '1rem',
        flexShrink: 0,
    },
    pagoFila: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.85rem 1.25rem',
        gap: '1rem',
    },
    pagoInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        flex: 1,
    },
    pagoMonto: {
        color: '#48BB78',
        fontWeight: 700,
        fontSize: '1rem',
    },
    pagoMet: {
        color: '#888',
        fontSize: '0.85rem',
    },
    pagoVendedor: {
        color: '#555',
        fontSize: '0.78rem',
    },
    btnAgregar: {
        background: '#F5C800',
        color: '#111',
        border: 'none',
        borderRadius: '8px',
        padding: '0.35rem 0.85rem',
        fontWeight: 700,
        fontSize: '0.85rem',
        cursor: 'pointer',
    },
    btnEliminar: {
        background: 'transparent',
        border: '1px solid rgba(229,62,62,0.3)',
        borderRadius: '7px',
        padding: '0.3rem 0.55rem',
        color: '#E53E3E',
        fontSize: '0.8rem',
        cursor: 'pointer',
        flexShrink: 0,
    },
    btnLiquidar: {
        background: '#48BB78',
        color: '#fff',
        border: 'none',
        borderRadius: '12px',
        padding: '1rem',
        fontWeight: 800,
        fontSize: '1.05rem',
        width: '100%',
        cursor: 'pointer',
    },
    aviso: {
        color: '#666',
        fontSize: '0.85rem',
        textAlign: 'center',
        marginTop: '0.75rem',
        marginBottom: 0,
    },
    filtroBar: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        marginBottom: '1rem',
        padding: '0.75rem 1rem',
        background: '#111',
        border: '1px solid #2E2E2E',
        borderRadius: '10px',
    },
    filtroBadge: {
        background: 'rgba(245,200,0,0.1)',
        border: '1px solid rgba(245,200,0,0.3)',
        borderRadius: '6px',
        color: '#F5C800',
        fontSize: '0.78rem',
        fontWeight: 700,
        padding: '0.2rem 0.6rem',
        flexShrink: 0,
    },
    campo: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
    },
    label: {
        color: '#C0C0C0',
        fontSize: '0.9rem',
        fontWeight: 600,
    },
};

const modal = {
    overlay: {
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '1rem',
    },
    card: {
        background: '#1A1A1A',
        border: '1px solid #2E2E2E',
        borderRadius: '16px',
        padding: '2rem',
        maxWidth: '400px',
        width: '100%',
    },
    titulo: {
        color: '#F0F0F0',
        fontWeight: 700,
        marginBottom: '1.25rem',
    },
    botones: {
        display: 'flex',
        gap: '0.75rem',
    },
    btnNo: {
        flex: 1,
        background: 'transparent',
        border: '1px solid #2E2E2E',
        borderRadius: '10px',
        padding: '0.8rem',
        color: '#888',
        fontWeight: 700,
        fontSize: '0.95rem',
        cursor: 'pointer',
    },
    btnSi: {
        flex: 1,
        background: '#F5C800',
        border: 'none',
        borderRadius: '10px',
        padding: '0.8rem',
        color: '#111',
        fontWeight: 800,
        fontSize: '0.95rem',
        cursor: 'pointer',
    },
    btnLiquidar: {
        flex: 1,
        background: '#48BB78',
        border: 'none',
        borderRadius: '10px',
        padding: '0.8rem',
        color: '#fff',
        fontWeight: 800,
        fontSize: '0.95rem',
        cursor: 'pointer',
    },
    btnEliminar: {
        flex: 1,
        background: '#E53E3E',
        border: 'none',
        borderRadius: '10px',
        padding: '0.8rem',
        color: '#fff',
        fontWeight: 800,
        fontSize: '0.95rem',
        cursor: 'pointer',
    },
};

export default LiquidarVueltas;
